import { ItemFilesService } from '@control-tower/platform-item-catalog';
import { inject, Injectable } from '@angular/core';
import {
    AbstractDocumentsFacade,
    documentsMapper,
    InFlightRequestCacheService,
} from '@pf/shared-services';
import {
    DocumentDto,
    DocumentRequirementLevelType,
    IDocument,
    IDocumentCreate,
    IDocumentDto,
    IDocumentSearchParams,
    IPagedResult,
    PFItemCatalogEntities,
} from '@pf/shared-common';
import { Operation } from 'fast-json-patch';
import { map, Observable, tap, throwError } from 'rxjs';
import { itemFileSetPrimaryPatchOperations } from '../infrastructure/items/item-file-patch.operations';

@Injectable()
export class ItemDocumentsFacade extends AbstractDocumentsFacade {
    private readonly inFlightRequestCache = inject(InFlightRequestCacheService);
    private readonly itemFilesService!: ItemFilesService;

    constructor() {
        const filesApi = inject(ItemFilesService);

        super(PFItemCatalogEntities.Item, {
            create$: (entityId: string, document: IDocumentCreate) =>
                filesApi.itemFilesPost(
                    { itemId: entityId, ...document },
                    'body',
                    false,
                    { transferCache: false }
                ) as Observable<IDocument>,
            delete$: (entityId: string, documentId: string) =>
                filesApi.itemFileDelete({
                    itemId: entityId,
                    id: documentId,
                }) as Observable<IDocument>,
            get$: (entityId: string, documentId: string) =>
                filesApi.itemFileEntityReadById({
                    itemId: entityId,
                    id: documentId,
                }) as Observable<IDocument>,
            search$: (entityId: string, params: IDocumentSearchParams) =>
                filesApi.itemFileEntitySearch({
                    itemId: entityId,
                    ...params,
                }) as Observable<IPagedResult<IDocument>>,
            download$: (entityId: string, documentId: string) =>
                filesApi.itemFileGet({
                    itemId: entityId,
                    id: documentId,
                }),
            patch$: (
                entityId: string,
                documentId: string,
                operations: readonly Operation[]
            ) =>
                filesApi.itemFileEntityUpdatePatch({
                    itemId: entityId,
                    id: documentId,
                    operation: [...operations],
                }) as Observable<IDocument>,
        });

        this.itemFilesService = filesApi;
    }

    getItemFilesFirstPage$(itemId: string): Observable<IDocument[]> {
        return this.itemFilesService
            .itemFileEntitySearch({
                itemId,
                pageNumber: 1,
                pageSize: 10,
            })
            .pipe(map(result => result.data ?? [])) as Observable<IDocument[]>;
    }

    createItemFile$(
        itemId: string,
        file: File | Blob,
        isDefault = false,
        fileName?: string
    ): Observable<IDocument> {
        const resolvedName =
            fileName ?? (file instanceof File ? file.name : undefined);

        const filePayload: File | Blob =
            file instanceof File
                ? file
                : resolvedName
                  ? new File([file], resolvedName, {
                        type: file.type || 'application/octet-stream',
                    })
                  : file;

        const dto = new DocumentDto({
            documentTypeId: '',
            documentTypeName: '',
            requirementLevel: DocumentRequirementLevelType.Unknown,
            file: filePayload as File,
            isDefault,
            ...(resolvedName ? { fileName: resolvedName } : {}),
        });

        return this.create$(itemId, dto).pipe(
            tap(() => this.invalidateDefaultItemImageCache(itemId)),
            map(documentDto => documentsMapper.toEntity(documentDto))
        );
    }

    setPrimaryItemImage$(
        itemId: string,
        fileId: string
    ): Observable<DocumentDto> {
        return this.patch$(
            itemId,
            fileId,
            itemFileSetPrimaryPatchOperations()
        ).pipe(tap(() => this.invalidateDefaultItemImageCache(itemId)));
    }

    override delete$(
        entityId: string,
        documentId: string,
        parentId?: string
    ): Observable<DocumentDto> {
        return super
            .delete$(entityId, documentId, parentId)
            .pipe(tap(() => this.invalidateDefaultItemImageCache(entityId)));
    }

    override update$(
        _entityId: string,
        _documentId: string,
        _modified: IDocumentDto,
        _parentId?: string
    ): Observable<DocumentDto> {
        return throwError(
            () =>
                new Error(
                    'Item file update is not supported via documents facade (base implementation would delete and recreate). Use setPrimaryItemImage$ for the default image.'
                )
        );
    }

    private invalidateDefaultItemImageCache(itemId: string): void {
        this.inFlightRequestCache.clear(`getDefaultItemImage$:${itemId}`);
    }
}
